import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface BlockedTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  blockedTime: {
    start: Date;
    end: Date;
    reason: string;
    recurring?: boolean;
    recurrenceEnd?: Date;
  } | null;
}

export const BlockedTimeModal: React.FC<BlockedTimeModalProps> = ({
  isOpen,
  onClose,
  blockedTime
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !blockedTime) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-hidden">
      <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
        <div className="h-full sm:h-auto w-full sm:max-w-md bg-white dark:bg-gray-800 sm:rounded-lg flex flex-col">
          <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sperrzeit Details</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Zeitraum</h4>
              <p className="mt-1 text-gray-900 dark:text-gray-100">
                {blockedTime.start.toLocaleDateString('de-DE', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                <br />
                {blockedTime.start.toLocaleTimeString('de-DE', { 
                  hour: '2-digit',
                  minute: '2-digit'
                })} - {blockedTime.end.toLocaleTimeString('de-DE', { 
                  hour: '2-digit',
                  minute: '2-digit'
                })} Uhr
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Grund</h4>
              <p className="mt-1 text-gray-900 dark:text-gray-100">{blockedTime.reason}</p>
            </div>

            {blockedTime.recurring && blockedTime.recurrenceEnd && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Wiederholung</h4>
                <p className="mt-1 text-gray-900 dark:text-gray-100">
                  Wöchentlich bis zum {blockedTime.recurrenceEnd.toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 sm:rounded-b-lg">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
